import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { ArrowLeft, Star, Search, CheckCircle, Send, MessageSquare } from "lucide-react";

const LOGO = "https://customer-assets.emergentagent.com/job_0890dc80-6146-4c2d-b11f-fa0b2de6d70b/artifacts/ekzdqal8_image.png";
const formatDate = (d) => d ? new Date(d).toLocaleDateString("id-ID", { day: "2-digit", month: "long", year: "numeric" }) : "";
const formatPrice = (p) => new Intl.NumberFormat("id-ID", { style: "currency", currency: "IDR", minimumFractionDigits: 0 }).format(p || 0);

function StarRating({ value, onChange, readonly }) {
  return (
    <div className="flex gap-1">
      {[1,2,3,4,5].map(s => (
        <button key={s} type="button" disabled={readonly} onClick={() => onChange?.(s)} className={`transition-colors ${readonly ? "cursor-default" : "cursor-pointer hover:scale-110"}`}>
          <svg className={`w-6 h-6 ${s <= value ? "text-yellow-400 fill-current" : "text-gray-600 fill-current"}`} viewBox="0 0 20 20">
            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
          </svg>
        </button>
      ))}
    </div>
  );
}

export default function TestimonialPage() {
  const [testimonials, setTestimonials] = useState([]);
  const [view, setView] = useState("list"); // list | verify | write | success
  const [txCode, setTxCode] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [txData, setTxData] = useState(null);
  const [form, setForm] = useState({ name: "", rating: 5, message: "" });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get("/testimonial/public").then(r => setTestimonials(r.data)).catch(() => {});
  }, []);

  const verifyCode = async (e) => {
    e.preventDefault();
    if (!txCode.trim()) return toast.error("Masukkan nomor pesanan dari struk");
    setVerifying(true);
    try {
      const { data } = await api.post("/testimonial/verify", { transaction_code: txCode.trim() });
      setTxData(data);
      setForm({ name: data.customer_name || "", rating: 5, message: "" });
      setView("write");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setVerifying(false); }
  };

  const submitTestimonial = async (e) => {
    e.preventDefault();
    if (!form.name || !form.message) return toast.error("Nama dan pesan wajib diisi");
    setSending(true);
    try {
      await api.post("/testimonial", { transaction_code: txData.transaction_code, ...form });
      setView("success");
      api.get("/testimonial/public").then(r => setTestimonials(r.data)).catch(() => {});
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSending(false); }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <nav className="fixed w-full z-50 bg-[#050505]/90 backdrop-blur-xl border-b border-[#262626]/50">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-14">
          <div className="flex items-center gap-3">
            <Link to="/"><img src={LOGO} alt="FI" className="w-8 h-8 rounded-full" /></Link>
            <span className="font-heading text-sm font-bold uppercase tracking-wider">Testimonial</span>
          </div>
          <Link to="/" className="text-sm text-gray-400 hover:text-white flex items-center gap-1"><ArrowLeft size={14} /> Beranda</Link>
        </div>
      </nav>

      <div className="pt-20 pb-16 px-6 md:px-12 lg:px-24 max-w-7xl mx-auto">

        {/* SUCCESS */}
        {view === "success" && (
          <div className="text-center py-20" data-testid="testimonial-success">
            <div className="w-16 h-16 rounded-full bg-green-900/30 flex items-center justify-center mx-auto mb-6">
              <CheckCircle size={32} className="text-green-400" />
            </div>
            <h1 className="font-heading text-3xl font-bold uppercase tracking-tight mb-3">Terima Kasih!</h1>
            <p className="text-gray-400 max-w-md mx-auto mb-8">Testimonial Anda telah berhasil dikirim. Terima kasih atas kepercayaan Anda kepada FI Barbershop Sekip!</p>
            <Button onClick={() => setView("list")} className="bg-[#E60000] hover:bg-[#FF1A1A] text-white font-bold uppercase rounded-none h-10" data-testid="back-to-list-btn">
              Lihat Testimonial Lainnya
            </Button>
          </div>
        )}

        {/* VERIFY STEP */}
        {view === "verify" && (
          <div data-testid="verify-view">
            <button onClick={() => setView("list")} className="text-sm text-gray-400 hover:text-white mb-6 flex items-center gap-1"><ArrowLeft size={14} /> Kembali</button>
            <div className="max-w-md mx-auto bg-[#0F0F0F] border border-[#262626] p-8">
              <h2 className="font-heading text-2xl font-bold uppercase tracking-tight text-white text-center mb-2">Tulis Testimonial</h2>
              <p className="text-gray-400 text-sm text-center mb-6">Masukkan nomor pesanan dari struk untuk verifikasi</p>
              <form onSubmit={verifyCode} className="space-y-4">
                <div>
                  <Label className="text-gray-300 text-xs font-bold uppercase">Nomor Pesanan (dari struk)</Label>
                  <Input className="mt-1 bg-[#1A1A1A] border-[#262626] text-white h-11 rounded-none text-center font-mono text-lg uppercase tracking-wider focus:border-[#E60000]" placeholder="FI-XXXXXXXX-XXXXXX" value={txCode} onChange={e => setTxCode(e.target.value.toUpperCase())} data-testid="tx-code-input" />
                </div>
                <Button type="submit" disabled={verifying} className="w-full bg-[#E60000] hover:bg-[#FF1A1A] text-white font-bold uppercase rounded-none h-11" data-testid="verify-btn">
                  <Search size={16} className="mr-2" /> {verifying ? "Memverifikasi..." : "Verifikasi"}
                </Button>
              </form>
            </div>
          </div>
        )}

        {/* WRITE STEP */}
        {view === "write" && txData && (
          <div data-testid="write-view">
            <button onClick={() => setView("verify")} className="text-sm text-gray-400 hover:text-white mb-6 flex items-center gap-1"><ArrowLeft size={14} /> Kembali</button>
            <div className="max-w-lg mx-auto bg-[#0F0F0F] border border-[#262626] p-8">
              {/* Transaction Info */}
              <div className="bg-[#0A0A0A] border border-[#1A1A1A] p-4 mb-6">
                <p className="text-[10px] text-gray-500 uppercase font-bold mb-2">Transaksi Terverifikasi</p>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-white font-mono text-sm font-bold">{txData.transaction_code}</p>
                    <p className="text-gray-400 text-xs mt-0.5">{txData.items?.join(", ")}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-[#E60000] font-bold">{formatPrice(txData.total)}</p>
                    <p className="text-gray-500 text-[10px]">{formatDate(txData.created_at)}</p>
                  </div>
                </div>
              </div>

              <h2 className="font-heading text-xl font-bold uppercase text-white mb-4">Tulis Testimonial Anda</h2>
              <form onSubmit={submitTestimonial} className="space-y-4" data-testid="testimonial-form">
                <div>
                  <Label className="text-gray-300 text-xs font-bold uppercase">Nama</Label>
                  <Input className="mt-1 bg-[#1A1A1A] border-[#262626] text-white h-10 rounded-none focus:border-[#E60000]" value={form.name} onChange={e => setForm({...form, name: e.target.value})} required data-testid="testi-name" />
                </div>
                <div>
                  <Label className="text-gray-300 text-xs font-bold uppercase mb-2 block">Rating</Label>
                  <StarRating value={form.rating} onChange={v => setForm({...form, rating: v})} />
                </div>
                <div>
                  <Label className="text-gray-300 text-xs font-bold uppercase">Pesan / Pengalaman</Label>
                  <textarea className="mt-1 w-full bg-[#1A1A1A] border border-[#262626] text-white p-3 rounded-none min-h-[120px] text-sm focus:border-[#E60000] focus:outline-none" placeholder="Ceritakan pengalaman Anda di FI Barbershop..." value={form.message} onChange={e => setForm({...form, message: e.target.value})} required data-testid="testi-message" />
                </div>
                <Button type="submit" disabled={sending} className="w-full bg-[#E60000] hover:bg-[#FF1A1A] text-white font-bold uppercase rounded-none h-11" data-testid="testi-submit">
                  <Send size={16} className="mr-2" /> {sending ? "Mengirim..." : "Kirim Testimonial"}
                </Button>
              </form>
            </div>
          </div>
        )}

        {/* LIST VIEW */}
        {view === "list" && (
          <>
            <div className="text-center mb-10">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#E60000] mb-2">TESTIMONIAL</p>
              <h1 className="font-heading text-4xl md:text-5xl font-bold uppercase tracking-tight" data-testid="testimonial-title">Kata Pelanggan Kami</h1>
              <p className="text-gray-400 mt-3 max-w-lg mx-auto">Testimonial langsung dari pelanggan FI Barbershop Sekip Palembang.</p>
              <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
                <Button onClick={() => setView("verify")} className="bg-[#E60000] hover:bg-[#FF1A1A] text-white font-bold uppercase rounded-none h-10 px-6" data-testid="write-testimonial-btn">
                  <MessageSquare size={16} className="mr-2" /> Tulis Testimonial
                </Button>
                <a href="https://search.google.com/local/writereview?placeid=ChIJ58zpU4B2Oy4RHCbmRBKUaak" target="_blank" rel="noopener noreferrer" data-testid="write-google-review-btn">
                  <Button className="bg-white/5 hover:bg-white/10 border border-[#262626] text-white font-bold uppercase rounded-none h-10 px-6 w-full">
                    <Star size={16} className="mr-2 text-yellow-400" /> Tulis Review Google
                  </Button>
                </a>
              </div>
            </div>

            {testimonials.length === 0 ? (
              <div className="text-center py-16 bg-[#0F0F0F] border border-[#262626]">
                <Star size={48} className="text-gray-700 mx-auto mb-4" />
                <p className="text-gray-400">Belum ada testimonial</p>
                <p className="text-gray-500 text-sm mt-1">Jadilah yang pertama memberikan testimonial!</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="testimonial-list">
                {testimonials.map((t, i) => (
                  <div key={t.id || i} className="bg-[#0F0F0F] border border-[#1A1A1A] p-6 hover:border-[#E60000]/30 transition-colors" data-testid={`testi-card-${i}`}>
                    <div className="flex gap-1 mb-4">
                      {[1,2,3,4,5].map(s => (
                        <svg key={s} className="w-4 h-4 text-yellow-400 fill-current" viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" /></svg>
                      ))}
                    </div>
                    <p className="text-gray-300 text-sm leading-relaxed mb-5">"{t.message}"</p>
                    <div className="pt-4 border-t border-[#1A1A1A]">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-[#E60000]/20 flex items-center justify-center text-[#E60000] font-bold text-sm">{t.name?.charAt(0)}</div>
                        <div>
                          <p className="text-white text-sm font-semibold">{t.name}</p>
                          <p className="text-gray-500 text-xs">{t.service && <>{t.service} &middot; </>}{formatDate(t.created_at)}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
